import { auth } from '@/src/firebase';
import { useCompanyContext } from '@/src/app/context/CompanyContext';
import { CompanySource } from '@/src/app/Interfaces/admin/User.interfaces';
import { useScreenWidthDetector } from '@/src/app/hooks/useScreenWidthDetector';
import { Tooltip } from 'primereact/tooltip';
import React, { useEffect, useRef, useState } from 'react';
import { Resizable } from 're-resizable';
import { useNavigate } from 'react-router-dom';

const MARGIN_X = 32;
const DESKTOP_CHROME_Y = 110;
const DESKTOP_MAX = { width: 1300, height: 800 };
const DESKTOP_MIN = { width: 300, height: 400 };
const defDesktopSize = { width: 355, height: 600 };

const MOBILE_WIDTH = 'calc(100dvw - 2rem)';
const MOBILE_CHROME_Y = 112;

const getViewport = () => ({
  width: window.visualViewport?.width ?? window.innerWidth,
  height: window.visualViewport?.height ?? window.innerHeight,
});

const getDesktopMax = () => {
  const vp = getViewport();
  return {
    width: Math.min(DESKTOP_MAX.width, Math.round(vp.width - MARGIN_X)),
    height: Math.min(
      DESKTOP_MAX.height,
      Math.round(vp.height - DESKTOP_CHROME_Y)
    ),
  };
};

const ChatbotIcon = () => {
  const { isMobile } = useScreenWidthDetector();
  const { selectedCompany } = useCompanyContext();
  const [open, setOpen] = useState(false);
  const [hasOpened, setHasOpened] = useState(false);
  const [frameSize, setFrameSize] = useState(defDesktopSize);
  const [desktopMax, setDesktopMax] = useState(getDesktopMax);
  const [mobileViewport, setMobileViewport] = useState({
    width: 0,
    height: 0,
    offsetX: 0,
    offsetY: 0,
  });
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const navigate = useNavigate();

  useEffect(() => {
    const vv = window.visualViewport;
    if (!isMobile || !vv) return;

    const update = () => {
      const layoutWidth = document.documentElement.clientWidth;
      const layoutHeight = document.documentElement.clientHeight;
      const offsetX = vv.offsetLeft - (layoutWidth - vv.width);
      const offsetY = vv.offsetTop - (layoutHeight - vv.height);
      setMobileViewport({
        width: vv.width,
        height: vv.height,
        offsetX,
        offsetY,
      });
    };
    update();
    vv.addEventListener('resize', update);
    vv.addEventListener('scroll', update);
    return () => {
      vv.removeEventListener('resize', update);
      vv.removeEventListener('scroll', update);
    };
  }, [isMobile]);

  const handleToggle = () => {
    if (!hasOpened) setHasOpened(true);
    setOpen((prev) => !prev);
  };

  useEffect(() => {
    if (isMobile) return;

    const initial = getDesktopMax();
    setDesktopMax(initial);
    setFrameSize({
      width: Math.min(defDesktopSize.width, initial.width),
      height: Math.min(defDesktopSize.height, initial.height),
    });

    const update = () => {
      const max = getDesktopMax();
      setDesktopMax(max);
      setFrameSize((prev) => ({
        width: Math.min(prev.width, max.width),
        height: Math.min(prev.height, max.height),
      }));
    };
    window.addEventListener('resize', update);
    window.visualViewport?.addEventListener('resize', update);
    return () => {
      window.removeEventListener('resize', update);
      window.visualViewport?.removeEventListener('resize', update);
    };
  }, [isMobile]);

  useEffect(() => {
    const handler = async (e: MessageEvent) => {
      if (e.origin !== process.env['NX_CHATBOT_ORIGIN']) return;
      if (e.data?.type !== 'WIDGET_READY') return;

      const token = await auth.currentUser?.getIdToken();
      if (!token || !iframeRef.current?.contentWindow) return;

      const targetOrigin = process.env['NX_CHATBOT_LINK'] ?? '';

      iframeRef.current.contentWindow.postMessage(
        { type: 'AUTH_TOKEN', token },
        targetOrigin
      );

      if (selectedCompany?.source === CompanySource.ViewPoint) {
        iframeRef.current.contentWindow.postMessage(
          {
            type: 'COMPANY_SEED',
            companyId: Number(selectedCompany.code),
            companyName: selectedCompany.name,
          },
          targetOrigin
        );
      }
    };

    window.addEventListener('message', handler);
    return () => window.removeEventListener('message', handler);
  }, [selectedCompany]);

  useEffect(() => {
    const reject = (reason: string, data: unknown) => {
      console.warn(`NAVIGATE rejected: ${reason}`, data);
    };

    const handler = (e: MessageEvent) => {
      if (e.origin !== process.env['NX_CHATBOT_ORIGIN']) return;
      if (e.source !== iframeRef.current?.contentWindow) return;
      if (e.data?.type !== 'NAVIGATE') return;

      const { path } = e.data;
      if (typeof path !== 'string') {
        return reject('path is not a string', e.data);
      }

      let url: URL;
      try {
        url = new URL(path, window.location.origin);
      } catch {
        return reject('unparseable path', path);
      }

      if (url.origin !== window.location.origin) {
        return reject('cross-origin path', path);
      }

      navigate(url.pathname + url.search);
    };

    window.addEventListener('message', handler);
    return () => window.removeEventListener('message', handler);
  }, [navigate]);

  const frameContent = (
    <div className="chatbotFrame border-round-xl overflow-hidden flex flex-column w-full h-full dashboardBG">
      <div className="bluwaiBlue text-white flex align-items-center justify-content-between px-3 py-2 flex-shrink-0">
        <div className="flex align-items-center gap-1">
          <span className="text-18px">Assistant</span>
          <a
            id="chatbotExpand"
            href={process.env['NX_CHATBOT_ORIGIN']}
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Open chatbot in a new window"
            data-pr-position="top"
            data-pr-tooltip="Open in new window"
            className="text-white cursor-pointer p-1 flex align-items-center"
          >
            <i className="pi pi-external-link text-sm" />
          </a>
        </div>
        <Tooltip target="#chatbotExpand" />
        <button
          id="chatbotClose"
          type="button"
          aria-label="Close chat"
          data-pr-position="left"
          data-pr-tooltip="Close chat"
          className="text-white bg-transparent border-none cursor-pointer p-1 flex align-items-center"
          onClick={() => setOpen(false)}
        >
          <i className="pi pi-times text-lg" />
        </button>
        <Tooltip target="#chatbotClose" />
      </div>
      <iframe
        title="Chatbot App"
        ref={iframeRef}
        src={process.env['NX_CHATBOT_LINK']}
        className="border-none w-full flex-1"
      />
    </div>
  );

  return (
    <React.Fragment>
      <div
        className="fixed bottom-0 right-0 mb-4 mr-4 flex flex-column align-items-end gap-2"
        style={{
          zIndex: 1100,
          transform: isMobile
            ? `translate(${mobileViewport.offsetX}px, ${mobileViewport.offsetY}px)`
            : undefined,
        }}
      >
        {hasOpened &&
          (isMobile ? (
            <div
              className={open ? 'block' : 'hidden'}
              style={{
                width: mobileViewport.width
                  ? mobileViewport.width - MARGIN_X
                  : MOBILE_WIDTH,
                height: mobileViewport.height
                  ? mobileViewport.height - MOBILE_CHROME_Y
                  : `calc(100dvh - ${MOBILE_CHROME_Y}px)`,
              }}
            >
              {frameContent}
            </div>
          ) : (
            <Resizable
              size={frameSize}
              onResizeStop={(_e, _direction, _ref, delta) =>
                setFrameSize((prev) => ({
                  width: prev.width + delta.width,
                  height: prev.height + delta.height,
                }))
              }
              minWidth={Math.min(DESKTOP_MIN.width, desktopMax.width)}
              minHeight={Math.min(DESKTOP_MIN.height, desktopMax.height)}
              maxWidth={desktopMax.width}
              maxHeight={desktopMax.height}
              enable={{ top: true, left: true, topLeft: true }}
              className={open ? 'block' : 'hidden'}
            >
              {frameContent}
            </Resizable>
          ))}
        <div
          id="chatbotIcon"
          aria-label="Open chatbot assistant"
          className="bluwaiBlue text-white flex align-items-center justify-content-center border-circle w-4rem h-4rem cursor-pointer shadow-4"
          data-pr-position="left"
          data-pr-tooltip="Chat with the assistant"
          onClick={handleToggle}
        >
          <i className="pi pi-comments text-2xl" />
        </div>
      </div>
      <Tooltip target={`#chatbotIcon`} />
    </React.Fragment>
  );
};

export default ChatbotIcon;
